import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import striptags from 'striptags';
import { client } from 'cccisd-apollo';
import PlatformWithModules from 'cccisd-laravel-assignment/dist/components/navigations/withModules/Mediator/index.js';
import Loader from 'cccisd-loader';
import CoachInfo from './components/CoachInfo';
import OtherSurveyInfo from './components/OtherSurveyInfo';
import initialDataQuery from './initialData.graphql';
import style from './style.css';
import _get from 'lodash/get';
import _set from 'lodash/set';

const dummyIntialData = {
    coach: {
        user: {
            firstName: 'Bill',
            lastName: 'Bowerman',
            username: 'bill@bowerman.dead',
            email: 'bill@bowerman.dead',
        },
        fields: {
            bio:
                'William Jay Bowerman (February 19, 1911 – December 24, 1999)' +
                ' was an American track and field coach and co-founder of Nike, Inc. ' +
                'Over his career, he trained 31 Olympic athletes, 51 All-Americans, ' +
                '12 American record-holders, 22 NCAA champions and 16 sub-4 minute milers.[1]',
        },
    },
};

/*
* APP SETUP:
*   1. Course Wrapper turned on for main course
*   2. Custom withModules selected for Course tab
*   3. set modules in settings JSON course.navigationSettings:
        "navigationSettings": {
            "mustTakeInOrder": false,
            "modules": [
                {
                    "language": "en",
                    "id": 1,
                    "title": "Introduction",
                    "header": null,
                    "overview": "This is the module 1 JSON overview."
                },
                ...
            ]
        }
*   4. add keys {"moduleId": <Int>, "lessonCardTitle": <string>} to each
        survey settings
*   5. add deploymentHandles "course", "teencourse", and "staywell" to
8       their respective deployments
*   6. add assignment handles to quests "course", "mealPlanning",
        "mealTracking", and "symptomChecklist"
*   7. mealPlanning, mealTracking, and symptomChecklist have repeatable
        deployments; set a deployment settings key {"isRepeated": true}
*   8. Client site registration deployment must be made with the
        demographics survey. deploymentHandle is "clientSiteRegistration"
*   9. "Allowed to edit answers" must be turned on for mealPlanning,
        mealTracking, and the site registration deployment (demographics),
        or else Print Mode doesn't work.
*   10. In the mealPlanning and mealTracking surveys, you must set the date
        element to have the appropriate "Default value from variable"
*   11. Apply the dev_tags "meal_planning_date",
        "meal_tracking_date", "symptom_checklist_binges",
        "symptom_checklist_purges", and "symptom_checklist_concerns"
        to their respective fields in meals and symptom surveys
*   12. Create 2 reports with handles "symptomChecklist" and "courseData".
        add text and headers via Reports GUI
*   13. Create resource center with handle "instructorResources",
        or process appdefs with --force-file flag
*   14. Add devTags for main course survey (See Chelsea's Word document,
        should be a few hundred of them)
*/

const Fortress = window.cccisd.fortress;

const WithModulesMediator = props => {
    const { symptomSurveyData, flowList } = props;
    const isPreviewMode = props.isPreviewMode || flowList.some(f => f.isPreviewMode);
    const isCoach =
        Fortress.user.acting.role.handle === 'instructor' || Fortress.user.acting.role.handle === 'siteAdmin';

    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [initialData, setInitialData] = useState(null);
    const [program, setProgram] = useState(null);

    useEffect(() => {
        async function getInitialData() {
            setIsInitialLoading(true);
            const response = await client.query({
                query: initialDataQuery,
                fetchPolicy: 'network-only',
            });
            if (isPreviewMode) {
                setInitialData(dummyIntialData);
            } else {
                setInitialData({ coach: response.data.roles.learner.parentRoles.instructor });
            }
            setIsInitialLoading(false);

            const programData = _get(response, 'data.roles.learner.fields.program', {});

            setProgram(programData);
        }

        if (isCoach) {
            setIsInitialLoading(false);
            setInitialData(true);
        } else {
            getInitialData();
        }
    }, []);

    const coachAccepted = Fortress.user.acting.role_data.coachAcceptedAt;

    if (isInitialLoading || !initialData) {
        return <Loader loading />;
    }

    const isCoached = ['teenCoached', 'adult'].includes(program) ? 1 : 2;
    return (
        <>
            {!isCoach && coachAccepted !== null ? <CoachInfo data={initialData && initialData.coach} /> : null}
            {!isCoach && <OtherSurveyInfo isPreviewMode={isPreviewMode} symptomSurveyData={symptomSurveyData} />}
            <div className={classnames(style.dashboardSection, style.borderedLight)}>
                <PlatformWithModules
                    {...{
                        ...props,
                        flowList: props.flowList.map(f => {
                            const title = _get(f, 'flowSettings.playerTemplateSettings.title');
                            const strippedTitle = title?.content ? striptags(title.content) : striptags(title);
                            _set(
                                f,
                                'flowSettings.playerTemplateSettings.title',
                                strippedTitle.trim().replace(/&nbsp;/g, '')
                            );

                            return {
                                ...f,
                                variables: { isCoached },
                            };
                        }),
                    }}
                />
            </div>
        </>
    );
};

export default WithModulesMediator;
